/**
 * 跨源隔离（COOP/COEP）探测、Service Worker 注入与 Worker 传输降级策略。
 *
 * 设计依据：DESIGN.md §2.7。`SharedArrayBuffer` 要求页面处于跨源隔离状态
 * （`Cross-Origin-Opener-Policy: same-origin` + `Cross-Origin-Embedder-Policy: require-corp`）。
 *
 * 两种部署形态：
 * - **自托管**：服务端直接下发响应头（本仓库的 `vite.config.ts` 对 dev server 与 preview 都配置了）。
 * - **静态托管**（GitHub Pages 等无法设置响应头）：由 `public/coi-sw.js` 这个 Service Worker
 *   拦截 `fetch` 并注入响应头，页面首次加载后需要 reload 一次才会进入隔离状态。
 *
 * 无论哪条路径失败，都必须能继续工作：`capabilities.sharedArrayBuffer === false` 时，
 * Worker 通信自动切换到 transferable `ArrayBuffer`（见 {@link createWorkerTransport}）。
 *
 * 本模块位于最底层（platform），不依赖任何其他层，且所有对宿主环境的访问都通过
 * {@link CrossOriginIsolationEnvironment} 注入，以便在 node 下做纯逻辑测试。
 */

/** 会话级标记键：记录本次会话是否已经为了启用隔离而 reload 过一次。 */
export const RELOAD_GUARD_KEY = 'photosmith.coi.reload-attempted';

/** 默认的 Service Worker 脚本文件名（相对于站点 base 路径）。 */
export const DEFAULT_SERVICE_WORKER_FILE = 'coi-sw.js';

/**
 * 等待 Service Worker 取得页面控制权的上限（毫秒）。
 *
 * `register()` 的 Promise 在**注册**成功时就 resolve，此时 SW 往往还处于
 * installing，尚未 activate、更未 claim 当前页面。此刻若立即 reload，新页面的
 * 导航请求不经过 SW，仍然拿不到注入的 COOP/COEP，而防循环标记已经落盘，
 * 于是整个会话被永久判定为「刷新过也没用」而降级。必须等它真正接管再刷新。
 */
export const CONTROLLER_WAIT_TIMEOUT_MS = 10_000;

/** 宿主环境中与跨源隔离相关的只读探针。 */
export interface IsolationProbe {
  /** `globalThis.crossOriginIsolated`。非浏览器环境视为 `false`。 */
  readonly crossOriginIsolated: boolean;
  /** 全局是否存在可构造的 `SharedArrayBuffer`。 */
  readonly hasSharedArrayBuffer: boolean;
  /** `isSecureContext`。Service Worker 注册的前提。 */
  readonly isSecureContext: boolean;
}

/** 平台能力集合。所有字段都是布尔量，调用方据此选择实现路径。 */
export interface PlatformCapabilities {
  /** 页面是否处于跨源隔离状态。 */
  readonly crossOriginIsolated: boolean;
  /**
   * 是否可以在 Worker 之间零拷贝共享 tile 像素。
   * 为 `false` 时必须走 transferable `ArrayBuffer` 路径。
   */
  readonly sharedArrayBuffer: boolean;
  /**
   * wasm 线程（`memory.shared`）是否可用。前提与 `sharedArrayBuffer` 相同，
   * 单独导出是为了让调用点的意图更清楚。
   */
  readonly wasmThreads: boolean;
}

/**
 * 从探针推导能力集合。
 *
 * `SharedArrayBuffer` 在部分非浏览器宿主（如 node）中即使没有隔离也可构造，
 * 这里坚持按浏览器语义判定：**必须同时满足构造函数存在与跨源隔离**，
 * 否则一份代码在 node 下测试通过、在浏览器里却崩在 `new SharedArrayBuffer`。
 */
export function detectCapabilities(probe: IsolationProbe): PlatformCapabilities {
  const shared = probe.hasSharedArrayBuffer && probe.crossOriginIsolated;
  return {
    crossOriginIsolated: probe.crossOriginIsolated,
    sharedArrayBuffer: shared,
    wasmThreads: shared,
  };
}

/** 读取当前全局对象的探针值。在任意宿主下都不抛异常。 */
export function readGlobalProbe(): IsolationProbe {
  const g = globalThis as {
    crossOriginIsolated?: boolean;
    isSecureContext?: boolean;
    SharedArrayBuffer?: unknown;
  };
  return {
    crossOriginIsolated: g.crossOriginIsolated === true,
    hasSharedArrayBuffer: typeof g.SharedArrayBuffer === 'function',
    isSecureContext: g.isSecureContext === true,
  };
}

/**
 * 当前进程的能力快照，在模块首次求值时确定。
 *
 * 之所以是快照而不是 getter：跨源隔离状态在一个文档的生命周期内不会变化
 * （变化必然伴随一次导航），把它冻结下来可以让热路径上的分支被 JIT 稳定优化。
 */
export const capabilities: PlatformCapabilities = detectCapabilities(readGlobalProbe());

/* ------------------------------------------------------------------ */
/* Service Worker 引导                                                  */
/* ------------------------------------------------------------------ */

/** 最小化的 `sessionStorage` 接口，只用到防循环标记需要的两个方法。 */
export interface SessionFlagStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** 最小化的 `navigator.serviceWorker` 接口。 */
export interface ServiceWorkerRegistrar {
  /** 已经控制当前页面的 SW，没有则为 `null`。 */
  readonly controller: unknown;
  register(scriptUrl: string, options: { scope: string }): Promise<unknown>;
  /** `controllerchange`：SW 取得控制权时触发。老浏览器可能缺失，故为可选。 */
  addEventListener?(type: 'controllerchange', listener: () => void): void;
  removeEventListener?(type: 'controllerchange', listener: () => void): void;
}

/** 引导流程需要的全部宿主依赖，全部可注入。 */
export interface CrossOriginIsolationEnvironment {
  readonly probe: IsolationProbe;
  /** 私密模式下 `sessionStorage` 可能不可用，此时必须放弃 reload。 */
  readonly sessionFlags: SessionFlagStore | undefined;
  readonly serviceWorker: ServiceWorkerRegistrar | undefined;
  /** SW 脚本 URL（含 base 前缀）。 */
  readonly scriptUrl: string;
  /** SW 作用域，通常是站点 base 路径。 */
  readonly scope: string;
  /**
   * 等待 SW 取得对当前页面的控制权，返回是否在超时前拿到。
   *
   * 之所以做成注入点而不是在函数体里监听 `controllerchange`：等待逻辑涉及事件
   * 与定时器，注入后测试可以直接给出「立刻接管」「超时」两种结果，无需假 DOM。
   */
  waitForController(timeoutMs: number): Promise<boolean>;
  /** 触发一次页面刷新。 */
  reload(): void;
  /** 诊断输出。降级本身不是错误，所以用 warn 而不是 throw。 */
  warn(message: string, cause?: unknown): void;
}

/**
 * 引导结果。除 `reloading` 外都表示「本次会话不会再触发刷新」，
 * 调用方可以立即按 {@link PlatformCapabilities} 选择实现路径。
 */
export type IsolationBootstrapOutcome =
  /** 已经隔离（服务端下发了响应头，或上一次 reload 后 SW 已生效）。 */
  | 'already-isolated'
  /** 已注册 SW 并即将刷新页面；本次会话最多发生一次。 */
  | 'reloading'
  /** 已经刷新过一次仍未隔离，放弃并降级，避免刷新循环。 */
  | 'gave-up-after-reload'
  /** 缺少防循环所需的 sessionStorage，不敢刷新，直接降级。 */
  | 'no-reload-guard'
  /** SW 注册成功但迟迟未接管页面，放弃本次刷新（不留标记，下次加载可再试）。 */
  | 'controller-timeout'
  /** 环境不支持（非安全上下文 / 无 Service Worker / 无 SharedArrayBuffer）。 */
  | 'unsupported'
  /** SW 注册失败。 */
  | 'registration-failed';

export interface IsolationBootstrapResult {
  readonly outcome: IsolationBootstrapOutcome;
  /** 引导结束时（不含刷新后）的能力集合。 */
  readonly capabilities: PlatformCapabilities;
  /** 本次调用是否请求了页面刷新。 */
  readonly reloadRequested: boolean;
}

function result(
  outcome: IsolationBootstrapOutcome,
  probe: IsolationProbe,
  reloadRequested: boolean,
): IsolationBootstrapResult {
  return { outcome, capabilities: detectCapabilities(probe), reloadRequested };
}

/**
 * 确保页面处于跨源隔离状态；做不到就明确降级。
 *
 * 防循环机制由三重保险构成：
 * 1. 已隔离时**清除**会话标记并直接返回，不注册也不刷新；
 * 2. 会话标记存在（说明本会话已经为此刷新过一次）时直接返回 `gave-up-after-reload`；
 * 3. 会话存储不可用时根本不刷新（返回 `no-reload-guard`），宁可慢也不进死循环。
 *
 * @returns 引导结果。`reloading` 时调用方应停止后续初始化。
 */
export async function ensureCrossOriginIsolation(
  env: CrossOriginIsolationEnvironment,
): Promise<IsolationBootstrapResult> {
  const { probe } = env;

  if (probe.crossOriginIsolated) {
    // 清除标记：下一次会话内若隔离状态丢失（例如切换到未配置响应头的部署），仍允许再试一次。
    env.sessionFlags?.removeItem(RELOAD_GUARD_KEY);
    return result('already-isolated', probe, false);
  }

  if (!probe.hasSharedArrayBuffer) {
    // 连构造函数都被移除的环境，注入响应头也换不来共享内存。
    env.warn('本环境不提供 SharedArrayBuffer，Worker 通信使用 transferable ArrayBuffer 路径。');
    return result('unsupported', probe, false);
  }

  if (!probe.isSecureContext || env.serviceWorker === undefined) {
    env.warn('非安全上下文或不支持 Service Worker，无法注入 COOP/COEP，降级为非隔离路径。');
    return result('unsupported', probe, false);
  }

  const flags = env.sessionFlags;
  if (flags === undefined) {
    env.warn('sessionStorage 不可用，缺少防刷新循环的保险，放弃启用跨源隔离。');
    return result('no-reload-guard', probe, false);
  }

  if (flags.getItem(RELOAD_GUARD_KEY) !== null) {
    // 已经刷新过一次仍未隔离：多半是托管方剥掉了 SW 或页面被 iframe 嵌入。再刷新只会循环。
    env.warn('已尝试过一次刷新仍未进入跨源隔离，放弃并降级。');
    return result('gave-up-after-reload', probe, false);
  }

  try {
    await env.serviceWorker.register(env.scriptUrl, { scope: env.scope });
  } catch (cause) {
    env.warn('COI Service Worker 注册失败，降级为非隔离路径。', cause);
    return result('registration-failed', probe, false);
  }

  // 注册 ≠ 接管。SW 未控制本页时刷新是徒劳的，且会白白烧掉唯一一次刷新机会。
  if (env.serviceWorker.controller === null || env.serviceWorker.controller === undefined) {
    const controlled = await env.waitForController(CONTROLLER_WAIT_TIMEOUT_MS);
    if (!controlled) {
      // 不写标记：本次并未刷新，下次加载时 SW 多半已激活，应当允许再试。
      env.warn('COI Service Worker 已注册但未在超时内接管页面，本次降级，下次加载会重试。');
      return result('controller-timeout', probe, false);
    }
  }

  // 标记必须在 reload 之前落盘，否则刷新后读不到标记就会无限循环。
  flags.setItem(RELOAD_GUARD_KEY, '1');
  env.reload();
  return result('reloading', probe, true);
}

/**
 * 用当前浏览器全局对象构造引导环境。非浏览器宿主下返回的环境会走 `unsupported` 分支。
 *
 * @param baseUrl 站点 base 路径（以 `/` 结尾），静态托管在子目录时必须传入。
 */
export function createBrowserEnvironment(baseUrl = '/'): CrossOriginIsolationEnvironment {
  const g = globalThis as {
    navigator?: { serviceWorker?: ServiceWorkerRegistrar };
    sessionStorage?: SessionFlagStore;
    location?: { reload(): void };
    console?: { warn(...args: unknown[]): void };
  };

  let sessionFlags: SessionFlagStore | undefined;
  try {
    // 私密模式下访问 sessionStorage 本身就可能抛 SecurityError。
    sessionFlags = g.sessionStorage ?? undefined;
    sessionFlags?.getItem(RELOAD_GUARD_KEY);
  } catch {
    sessionFlags = undefined;
  }

  return {
    probe: readGlobalProbe(),
    sessionFlags,
    serviceWorker: g.navigator?.serviceWorker,
    scriptUrl: `${baseUrl}${DEFAULT_SERVICE_WORKER_FILE}`,
    scope: baseUrl,
    waitForController: (timeoutMs) => {
      const sw = g.navigator?.serviceWorker;
      if (sw === undefined) return Promise.resolve(false);
      if (sw.controller !== null && sw.controller !== undefined) return Promise.resolve(true);
      // 缺少事件接口的宿主无法判断接管时机，宁可报告失败也不要盲目刷新。
      // 取成局部常量而非闭包里访问 sw.addEventListener：可选方法的类型收窄
      // 跨不过闭包边界，直接调用在严格模式下不通过。
      const addListener = sw.addEventListener;
      const removeListener = sw.removeEventListener;
      if (addListener === undefined) return Promise.resolve(false);
      return new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (controlled: boolean): void => {
          if (settled) return;
          settled = true;
          removeListener?.call(sw, 'controllerchange', onControllerChange);
          clearTimeout(timer);
          resolve(controlled);
        };
        const onControllerChange = (): void => finish(true);
        const timer = setTimeout(() => finish(false), timeoutMs);
        addListener.call(sw, 'controllerchange', onControllerChange);
      });
    },
    reload: () => {
      g.location?.reload();
    },
    warn: (message, cause) => {
      if (cause === undefined) g.console?.warn(`[coi] ${message}`);
      else g.console?.warn(`[coi] ${message}`, cause);
    },
  };
}

/* ------------------------------------------------------------------ */
/* Worker 传输策略（隔离 / 非隔离两条路径）                              */
/* ------------------------------------------------------------------ */

/** 传输策略种类。 */
export type TransportKind = 'shared' | 'transfer';

/**
 * 一块交给 Worker 处理的字节缓冲。
 *
 * 单位是**字节**，不是像素；分块引擎按 tile（256×256×4 = 262144 字节）分配，
 * 这里不感知图像语义，也不提供任何「整图」尺寸的便捷分配。
 */
export interface WorkerBuffer {
  readonly byteLength: number;
  /** 底层缓冲是否为 `SharedArrayBuffer`。 */
  readonly shared: boolean;
  /**
   * 所有权是否已经转移给对端（仅 transfer 路径可能为 `true`）。
   * 转移后本端读写会抛错，这是有意的：静默读到空数据比崩溃更难查。
   */
  readonly detached: boolean;
  /** 字节视图。已转移时访问抛 `Error`。 */
  readonly bytes: Uint8Array;
  /** 底层缓冲对象。已转移时访问抛 `Error`。 */
  readonly raw: SharedArrayBuffer | ArrayBuffer;
}

/** 传输线上的消息封套。`meta` 由调用方定义，必须是可结构化克隆的值。 */
export interface TransportMessage<M> {
  readonly kind: TransportKind;
  readonly meta: M;
  readonly buffers: readonly (SharedArrayBuffer | ArrayBuffer)[];
}

/** `prepare` 的产物：消息本体 + `postMessage` 的第二参数。 */
export interface PreparedMessage<M> {
  readonly message: TransportMessage<M>;
  /** shared 路径下长度恒为 0。 */
  readonly transfer: Transferable[];
}

/** 对端收到消息后重建的缓冲。 */
export interface ReceivedMessage<M> {
  readonly meta: M;
  readonly buffers: readonly WorkerBuffer[];
}

/** `postMessage` 的最小接口，`Worker` / `MessagePort` / `DedicatedWorkerGlobalScope` 都满足。 */
export interface MessagePostTarget {
  postMessage(message: unknown, transfer: Transferable[]): void;
}

/**
 * Worker 传输策略。两个实现共享同一套 API，调用点不需要写 `if (sharedArrayBuffer)`。
 */
export interface WorkerTransport {
  readonly kind: TransportKind;
  /** 是否使用共享内存（等价于 `kind === 'shared'`，便于可读性）。 */
  readonly sharedArrayBuffer: boolean;
  /**
   * 处理完成后对端是否必须把缓冲发回来。
   * transfer 路径下所有权是单向转移的，不发回就永久丢失；shared 路径下为 `false`。
   */
  readonly requiresReturnTrip: boolean;
  /** 分配一块新缓冲，内容为全 0。 */
  allocate(byteLength: number): WorkerBuffer;
  /**
   * 用已有字节初始化一块缓冲（会复制一次；shared 路径下这次复制无法避免，
   * 因为普通 `ArrayBuffer` 的内容不能就地变成共享内存）。
   */
  from(bytes: Uint8Array): WorkerBuffer;
  /**
   * 打包消息。transfer 路径下会把传入的缓冲标记为已转移，
   * 调用方必须在 `postMessage` 之后不再触碰它们。
   */
  prepare<M>(meta: M, buffers: readonly WorkerBuffer[]): PreparedMessage<M>;
  /** 直接投递到目标端口。 */
  postTo<M>(target: MessagePostTarget, meta: M, buffers: readonly WorkerBuffer[]): void;
  /** 对端解包。 */
  adopt<M>(message: TransportMessage<M>): ReceivedMessage<M>;
}

class TransportBuffer implements WorkerBuffer {
  readonly shared: boolean;
  readonly byteLength: number;
  #raw: SharedArrayBuffer | ArrayBuffer | null;
  #bytes: Uint8Array | null;

  constructor(raw: SharedArrayBuffer | ArrayBuffer) {
    this.#raw = raw;
    this.#bytes = new Uint8Array(raw);
    this.byteLength = raw.byteLength;
    this.shared = typeof SharedArrayBuffer === 'function' && raw instanceof SharedArrayBuffer;
  }

  get detached(): boolean {
    return this.#raw === null;
  }

  get bytes(): Uint8Array {
    if (this.#bytes === null) throw new Error('WorkerBuffer 已转移给对端，不能再读写。');
    return this.#bytes;
  }

  get raw(): SharedArrayBuffer | ArrayBuffer {
    if (this.#raw === null) throw new Error('WorkerBuffer 已转移给对端，不能再读写。');
    return this.#raw;
  }

  /** 标记所有权已转移。只在 transfer 路径的 `prepare` 中调用。 */
  markTransferred(): void {
    this.#raw = null;
    this.#bytes = null;
  }
}

/** 共享内存路径：消息里传的是同一块 `SharedArrayBuffer`，两端看到同一份内存。 */
class SharedTransport implements WorkerTransport {
  readonly kind = 'shared' as const;
  readonly sharedArrayBuffer = true;
  readonly requiresReturnTrip = false;

  allocate(byteLength: number): WorkerBuffer {
    return new TransportBuffer(new SharedArrayBuffer(byteLength));
  }

  from(bytes: Uint8Array): WorkerBuffer {
    const buffer = new TransportBuffer(new SharedArrayBuffer(bytes.byteLength));
    buffer.bytes.set(bytes);
    return buffer;
  }

  prepare<M>(meta: M, buffers: readonly WorkerBuffer[]): PreparedMessage<M> {
    const raw: (SharedArrayBuffer | ArrayBuffer)[] = new Array(buffers.length);
    for (let i = 0; i < buffers.length; i++) {
      const b = buffers[i];
      if (b === undefined) throw new Error('buffers 中存在空洞。');
      raw[i] = b.raw;
    }
    // SharedArrayBuffer 不可 transfer，也不需要：结构化克隆会共享同一块内存。
    return { message: { kind: this.kind, meta, buffers: raw }, transfer: [] };
  }

  postTo<M>(target: MessagePostTarget, meta: M, buffers: readonly WorkerBuffer[]): void {
    const prepared = this.prepare(meta, buffers);
    target.postMessage(prepared.message, prepared.transfer);
  }

  adopt<M>(message: TransportMessage<M>): ReceivedMessage<M> {
    return { meta: message.meta, buffers: adoptRaw(message.buffers) };
  }
}

/** 降级路径：普通 `ArrayBuffer` + transfer list，零拷贝但所有权单向转移。 */
class TransferTransport implements WorkerTransport {
  readonly kind = 'transfer' as const;
  readonly sharedArrayBuffer = false;
  readonly requiresReturnTrip = true;

  allocate(byteLength: number): WorkerBuffer {
    return new TransportBuffer(new ArrayBuffer(byteLength));
  }

  from(bytes: Uint8Array): WorkerBuffer {
    const buffer = new TransportBuffer(new ArrayBuffer(bytes.byteLength));
    buffer.bytes.set(bytes);
    return buffer;
  }

  prepare<M>(meta: M, buffers: readonly WorkerBuffer[]): PreparedMessage<M> {
    const count = buffers.length;
    const raw: (SharedArrayBuffer | ArrayBuffer)[] = new Array(count);
    const transfer: Transferable[] = new Array(count);
    for (let i = 0; i < count; i++) {
      const b = buffers[i];
      if (b === undefined) throw new Error('buffers 中存在空洞。');
      const underlying = b.raw;
      if (!(underlying instanceof ArrayBuffer)) {
        throw new Error('transfer 路径不接受 SharedArrayBuffer。');
      }
      raw[i] = underlying;
      transfer[i] = underlying;
    }
    // 先构造完消息再标记，避免中途抛错时留下半转移状态。
    for (let i = 0; i < count; i++) {
      const b = buffers[i];
      if (b instanceof TransportBuffer) b.markTransferred();
    }
    return { message: { kind: this.kind, meta, buffers: raw }, transfer };
  }

  postTo<M>(target: MessagePostTarget, meta: M, buffers: readonly WorkerBuffer[]): void {
    const prepared = this.prepare(meta, buffers);
    target.postMessage(prepared.message, prepared.transfer);
  }

  adopt<M>(message: TransportMessage<M>): ReceivedMessage<M> {
    return { meta: message.meta, buffers: adoptRaw(message.buffers) };
  }
}

function adoptRaw(raws: readonly (SharedArrayBuffer | ArrayBuffer)[]): readonly WorkerBuffer[] {
  const out: WorkerBuffer[] = new Array(raws.length);
  for (let i = 0; i < raws.length; i++) {
    const raw = raws[i];
    if (raw === undefined) throw new Error('消息的 buffers 中存在空洞。');
    out[i] = new TransportBuffer(raw);
  }
  return out;
}

/**
 * 按当前能力选择传输策略。
 *
 * @param caps 能力集合，默认用模块级快照 {@link capabilities}。
 *   `caps.sharedArrayBuffer === false` 时返回 transferable `ArrayBuffer` 实现，
 *   两者接口完全一致，调用点无需分支。
 */
export function createWorkerTransport(caps: PlatformCapabilities = capabilities): WorkerTransport {
  return caps.sharedArrayBuffer ? new SharedTransport() : new TransferTransport();
}
